import { DateInput } from "components/DateInput";
import { Form, Formik } from "formik";
import TimePickerField from "components/TimePicker/TimePicker";
import { Button } from "newui/components/Button";

import * as Yup from "yup";
import { useEffect, useRef, useState } from "react";
import { updateSubmissionDeadLine } from "actions/RFQ/rfqsAction";
import dayjs from "newui/utils/dayjs";
import {
	Drawer,
	DrawerBody,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
	Flex,
	Spinner,
} from "@chakra-ui/react";
import { showToast, timezoneChecker, SERVER_TIMEZONE } from "utils/helpers";
import { CloseButton } from "components/RFQ/CloseButton";
import { isNull } from "utils/validation";
import { FormattedInput } from "components/FormInputs/FormattedInput";
import { SubmissionDeadlineExtensionEmailContent } from "components/email-template/SubmissionDeadlineExtensionEmailContent";
import EmailPreview from "pages/EmailPreview/EmailPreview";
import { useRfxMail } from "components/email-template/hooks/useRfxMail";
import { isRfxOnboardingType } from "newui/features/Rfx/utils";

// PRO-656: `submissionDeadline` comes back from the API as an ISO-8601 UTC
// instant (Laravel's default `datetime` cast serialisation — no
// `serializeDate` override exists for this field) — it is NOT a naive
// SERVER_TIMEZONE wall-clock string, and must NOT be re-parsed with
// `dayjs.tz(value, SERVER_TIMEZONE)`; that would double-shift an already
// unambiguous instant. `dayjs(isoString)` alone parses it correctly but then
// renders in the BROWSER's local timezone, not the RFx's submission timezone
// — so the drawer showed the viewer's own local wall-clock time under
// whatever timezone label was stored, which coincidentally looked right only
// for a viewer in that same zone. The fix is to explicitly add the target
// zone rather than removing/restating a source zone. Exported (and pulled out
// of the component) so the date/time-together behaviour — the part a naive
// fix gets wrong at a midnight boundary — can be unit tested without mounting
// the Drawer/Formik tree.
export const computeDisplayDeadline = (submissionDeadline, submissionTimezone) => {
	if (!submissionDeadline) {
		return null;
	}
	return dayjs(submissionDeadline).tz(timezoneChecker(submissionTimezone));
};

const ExtendSubmissionDeadlineModal = ({
	isModalOpen,
	rfqDetail,
	onSubmissionDateExtension,
	onClose,
	// TASK-036 (typecheck sweep): defaulted — spread at the Drawer is a no-op
	// when callers (e.g. TendersQuotesList) pass no extra props.
	props = {},
}) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const formikRef = useRef();
	const [formValues, setFormValues] = useState();
	const [newDeadline, setNewDeadline] = useState();

	const { rfxMail, additionalMessage, handleAdditionalMessageChange } =
		useRfxMail(rfqDetail);

	const [newDate, setNewDateValue] = useState(
		rfxMail?.emailData?.deadline_date
	);
	const [newTime, setNewTimeValue] = useState(
		rfxMail?.emailData?.deadline_time
	);
	const [newTimeZone, setNewTimeZoneValue] = useState(
		rfxMail?.emailData?.deadline_time_zone
	);

	const handleOnClose = () => {
		setNewDeadline(null);
		if (onClose) {
			onClose();
		}
	};

	// Deriving BOTH the date and the time fields from this SAME converted
	// instant (rather than formatting them separately, as before) is
	// deliberate: a submission timezone ahead of Perth can push a near-midnight
	// time onto the next calendar day, and only sharing one converted instant
	// keeps that day shift consistent between the date field and the time
	// field. See `computeDisplayDeadline` above for the conversion itself.
	const displayDeadline = computeDisplayDeadline(
		rfqDetail?.submissionDeadline,
		rfqDetail?.submissionTimezone
	);

	const onSubmit = (formObject) => {
		// Interpret the picked date + time as wall-clock in the SELECTED
		// submission timezone, then re-express that instant in SERVER_TIMEZONE
		// before sending. The previous dayjs(localString) parsed in the
		// browser's zone and emitted a naive string, so the backend re-read it
		// in APP_TIMEZONE and stored the wrong instant (see data-processor.ts
		// for the same fix).
		const newDeadline = formValues?.extended_time
			? dayjs.tz(
					`${dayjs(formValues?.extended_date).format("YYYY-MM-DD")} ${formValues?.extended_time}`,
					"YYYY-MM-DD HH:mm",
					timezoneChecker(formValues?.submission_deadline_timezone)
				)
			: "";

		const data = {
			submission_deadline: newDeadline
				? newDeadline.tz(SERVER_TIMEZONE).format("YYYY-MM-DD HH:mm:ss")
				: "",
			submission_deadline_timezone: formValues.submission_deadline_timezone,
			additional_message: formValues?.additional_message,
		};

		setIsSubmitting(true);
		updateSubmissionDeadLine(rfqDetail.id, data)
			.then((res) => {
				onSubmissionDateExtension(res.data.data);
				handleOnClose();
				showToast("Submission Deadline Extended Successfully", "Success", true);
			})
			.catch((e) => {
				showToast(e.response?.data?.message, "Error");
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	const handleDateChange = (dateValue) => {
		setNewDateValue(dateValue);
	};

	const handleTimeChange = (timeString) => {
		setNewTimeValue(timeString);
	};

	const handleTimeZoneChange = (timeString) => {
		setNewTimeZoneValue(timeString);
	};

	useEffect(() => {
		const date = newDate ?? rfxMail?.emailData?.deadline_date;
		const time = newTime ?? rfxMail?.emailData?.deadline_24_time;
		const timeZone = newTimeZone ?? rfxMail?.emailData?.deadline_time_zone;

		const submissionDeadline =
			newTime || newDate || newTimeZone
				? dayjs(dayjs(date).format("YYYY-MM-DD") + " " + time + ":00").format(
						"dddd D MMMM YYYY h:mm A "
					) + timeZone
				: "";

		setNewDeadline(submissionDeadline);
	}, [
		newDate,
		newTime,
		newTimeZone,
		rfxMail?.emailData?.deadline_date,
		rfxMail?.emailData?.deadline_time,
		rfxMail?.emailData?.deadline_time_zone,
		rfxMail?.emailData?.deadline_24_time,
	]);

	const isOnboardingRfx = isRfxOnboardingType(
		rfqDetail?.rfxType ?? rfqDetail?.type
	);

	return (
		<>
			<Drawer
				placement={"right"}
				isOpen={isModalOpen}
				onClose={handleOnClose}
				size="xl"
				{...props}
				className="bg-white p-11"
			>
				<DrawerOverlay onClick={handleOnClose} />
				<DrawerContent maxWidth={"1280px"}>
					<DrawerHeader className="border-b-2">
						<Flex
							justifyContent="space-between"
							align={"center"}
						>
							<div>
								<p className="text-xl font-semibold text-bg-brand-solid">
									Submission Deadline Extension
								</p>
							</div>
							<CloseButton onClose={handleOnClose} />
						</Flex>
					</DrawerHeader>
					<DrawerBody>
						<div className="grid grid-cols-3">
							<div className="relative">
								<div className="fixed">
									<div>
										<Formik
											innerRef={formikRef}
											validationSchema={Yup.object().shape({
												extended_date: Yup.string().required(
													"This field is required"
												),
												extended_time: Yup.string().required(
													"This field is required"
												),
												additional_message: Yup.string().required(
													"This field is required"
												),
											})}
											initialValues={{
												extended_date: displayDeadline
													? displayDeadline.format("YYYY-MM-DD")
													: rfqDetail?.submissionDeadline,
												extended_time: displayDeadline
													? displayDeadline.format("HH:mm")
													: dayjs(
															rfqDetail?.submissionDeadline
														).format("HH:mm"),
												submission_deadline_timezone:
													rfqDetail?.submissionTimezone,
												additional_message: "",
											}}
											onSubmit={onSubmit}
										>
											{(props) => {
												const { setFieldValue, values } = props;
												setFormValues(values);
												return (
													<Form>
														<DateInput
															name="extended_date"
															label="Extended Deadline"
															setFieldValue={setFieldValue}
															values={values}
															minDate={
																displayDeadline
																	? displayDeadline.startOf("day").toDate()
																	: dayjs(
																			dayjs(
																				rfqDetail?.submissionDeadline
																			).format("YYYY/MM/D")
																		).toDate()
															}
															minDateTooltipContent="current Submission Deadline"
															onChange={handleDateChange}
														/>
														<TimePickerField
															className="mt-9 w-full flex-1"
															name="extended_time"
															nameTimezone="submission_deadline_timezone"
															label="Extended Time"
															onTimeChange={handleTimeChange}
															onTimeZoneChange={handleTimeZoneChange}
														/>
														<FormattedInput
															value={additionalMessage}
															label="Reason for Extension"
															name="additional_message"
															characterLimit={500}
															placeholder="Enter reason for extension to put in email"
															required
															error={
																isNull(values.additional_message) &&
																"Reason for Extension is required"
															}
															onChange={(e) => {
																handleAdditionalMessageChange(e);
																return setFieldValue("additional_message", e);
															}}
														/>
														<Button
															type="submit"
															btntype="submit"
															className="my-4 !w-full !py-3"
														>
															Extend Time
														</Button>
														{isSubmitting && (
															<div className="flex items-center justify-center gap-4 pt-4 text-royal-blue">
																<Spinner />
																<p className="text-center font-bold">
																	Submitting ....
																</p>
															</div>
														)}
													</Form>
												);
											}}
										</Formik>
									</div>
								</div>
							</div>
							<div className="col-span-2">
								<EmailPreview>
									<SubmissionDeadlineExtensionEmailContent
										rfxMail={rfxMail}
										newDeadline={newDeadline}
										isOnboarding={isOnboardingRfx}
									/>
								</EmailPreview>
							</div>
						</div>
					</DrawerBody>
				</DrawerContent>
			</Drawer>
		</>
	);
};

export default ExtendSubmissionDeadlineModal;
